import { useState } from "react";
import { IconBan } from "@tabler/icons-react";
import { Badge, EditRowActions, Input, Stack, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { OrganizacionAdmin } from "@/lib/types";
import { useDarDeBajaOrganizacion, useReactivarOrganizacion, useRenombrarOrganizacion } from "../hooks/use-organizaciones-mutations";

/**
 * Fila con toggle de edit · 3 acciones según estado:
 *   - Renombrar (siempre).
 *   - Dar de baja (si activa) · pide confirmación porque es destructivo
 *     para los nuevos ingresos (decisión 65).
 *   - Reactivar (si inactiva).
 */
export function OrganizacionRow({ org }: { org: OrganizacionAdmin }) {
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(org.nombre);
  const confirm = useConfirmDialog();

  const renombrar = useRenombrarOrganizacion({ onRenombrado: () => setEditando(false) });
  const baja = useDarDeBajaOrganizacion();
  const reactivar = useReactivarOrganizacion();

  const confirmarBaja = async () => {
    const ok = await confirm({
      title: `Dar de baja "${org.nombre}"`,
      message: (
        <Stack gap="xs">
          <Text size="sm">
            La organización quedará <b>INACTIVA</b>.
          </Text>
          <Text size="sm" c="dimmed">
            Las solicitudes existentes con esta organización se preservan, pero los usuarios ya
            no podrán crear solicitudes nuevas para ella.
          </Text>
          <Text size="sm" c="dimmed">
            Puedes reactivarla después si lo necesitas.
          </Text>
        </Stack>
      ),
      confirmLabel: "Dar de baja",
      confirmColor: "peligro",
      icon: <IconBan size={14} />,
    });
    if (ok) baja.mutate({ id: org.id, nombre: org.nombre });
  };

  const cancelarEdicion = () => {
    setNombre(org.nombre);
    setEditando(false);
  };

  if (editando) {
    return (
      <Tr className="bg-muted/50">
        <Td>
          <Input size="xs" value={nombre} onChange={(e) => setNombre(e.currentTarget.value)} autoFocus />
        </Td>
        <Td>
          <Text size="xs" ff="mono">
            {org.rfc}
          </Text>
        </Td>
        <Td>
          <Text size="xs" ff="mono">
            {org.numeroSociedad}
          </Text>
        </Td>
        <Td>
          <BadgeEstado activa={org.estaActiva} />
        </Td>
        <Td align="right">
          <EditRowActions mode="edit" saving={renombrar.isPending} onCancel={cancelarEdicion} onSave={() => renombrar.mutate({ id: org.id, nombre: nombre.trim() })} />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr>
      <Td>
        <Text fw={500} size="sm">
          {org.nombre}
        </Text>
      </Td>
      <Td>
        <Text size="xs" ff="mono">
          {org.rfc}
        </Text>
      </Td>
      <Td>
        <Text size="xs" ff="mono">
          {org.numeroSociedad}
        </Text>
      </Td>
      <Td>
        <BadgeEstado activa={org.estaActiva} />
      </Td>
      <Td align="right">
        <EditRowActions
          mode="view"
          onEdit={() => setEditando(true)}
          onDelete={org.estaActiva ? confirmarBaja : undefined}
          deleteVariant="baja"
          deleting={baja.isPending}
          reactivable={!org.estaActiva}
          onReactivar={() => reactivar.mutate({ id: org.id, nombre: org.nombre })}
          reactivando={reactivar.isPending}
        />
      </Td>
    </Tr>
  );
}

function BadgeEstado({ activa }: { activa: boolean }) {
  return (
    <Badge size="sm" color={activa ? "exito" : "slate"}>
      {activa ? "Activa" : "Inactiva"}
    </Badge>
  );
}
